import React, { useCallback, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api/schemas';
import { list as listOrganizations, load } from 'js/actions/org-actions';
import { list as listTeams } from 'js/actions/team-actions';
import { useQuery } from 'js/hooks/useQuery';
import { QueryBase } from 'js/store/reducers/queries';
import { selectActiveWorkspace } from 'js/store/reducers/workspaces';
import { orgManageOrgQueryId } from '../components/ManageOrg/orgRubricSchemesUtils';
import OrgPageLayout from '../components/ManageOrg/OrgPageLayout';
import OrgStructureContainer from './OrgStructureContainer';
import OrgWeightsContainer from './OrgWeightsContainer';
import OrgRubricSchemesContainer from './OrgRubricSchemesContainer';
import OrgTeamRubricCustomWeightsContainer from './OrgTeamRubricCustomWeightsContainer';

function OrgManageRouter({
  orgId,
  org,
  loadOrg,
  listTeams,
  listOrganizations,
  workspace,
}) {
  const { workspaceId } = useParams();
  const match = useRouteMatch();

  const baseUrl = `/w/${workspaceId}/orgs/${orgId}/manage`;
  const orgQueryId = useMemo(() => orgManageOrgQueryId(orgId), [orgId]);
  const teamsQueryId = `all-teams-${workspaceId}`;
  const workspaceOrgsQueryId = `${QueryBase.WorkspaceOrgs}-${workspaceId}`;

  const loadOrgQuery = useCallback(
    (qid) => loadOrg(orgId, qid),
    [loadOrg, orgId]
  );

  const {
    refresh: refreshOrg,
    isLoading: orgQueryLoading,
    hasLoaded: orgHasLoaded,
  } = useQuery({
    queryId: orgQueryId,
    query: loadOrgQuery,
    schema: SCHEMAS.ORGANIZATION,
  });

  const loadTeamsQuery = useCallback((qid) => listTeams({}, qid), [listTeams]);

  const { refresh: refreshTeams } = useQuery({
    queryId: teamsQueryId,
    query: loadTeamsQuery,
    schema: SCHEMAS.TEAM_ARRAY,
  });

  const loadOrganizationsQuery = useCallback(() => {
    if (!workspace?.id) {
      return Promise.resolve(null);
    }
    return listOrganizations(workspace.id);
  }, [listOrganizations, workspace?.id]);

  const { refresh: refreshOrganizations } = useQuery({
    queryId: workspaceOrgsQueryId,
    query: loadOrganizationsQuery,
  });

  useEffect(() => {
    refreshOrg();
    refreshTeams();
    refreshOrganizations();
  }, [refreshOrg, refreshTeams, refreshOrganizations]);

  const orgQueryError = useSelector(
    (state) => state.queries[orgQueryId]?.error
  );
  const loading = orgQueryLoading || !orgHasLoaded;
  const loadFailed = Boolean(orgQueryError);

  return (
    <Switch>
      <Route
        exact
        path={`${match.path}/rubrics/:rubricId/teams/:teamId/custom-weights`}
        component={OrgTeamRubricCustomWeightsContainer}
      />
      <Route
        path={match.path}
        render={() => (
          <OrgPageLayout
            organization={org}
            orgLoadFailed={loadFailed}
            loading={loading}
          >
            <Switch>
              <Route
                exact
                path={`${match.path}/weights`}
                render={() => (
                  <OrgWeightsContainer
                    organization={org}
                    loading={loading}
                    errorLoading={loadFailed}
                  />
                )}
              />
              <Route
                exact
                path={`${match.path}/rubrics/:rubricId?`}
                render={() => (
                  <OrgRubricSchemesContainer
                    organization={org}
                    loading={loading}
                    errorLoading={loadFailed}
                  />
                )}
              />
              <Route
                exact
                path={match.path}
                render={() => (
                  <OrgStructureContainer
                    orgId={orgId}
                    organization={org}
                    loading={loading}
                    errorLoading={loadFailed}
                    refreshOrg={refreshOrg}
                  />
                )}
              />
              <Redirect to={baseUrl} />
            </Switch>
          </OrgPageLayout>
        )}
      />
    </Switch>
  );
}

export default connect(
  (store, { match }) => {
    const workspaceId = match.params.workspaceId;
    const teamsQueryId = `all-teams-${workspaceId}`;
    return {
      orgId: match.params.orgId,
      org: denormalize(
        store.entities.organizations[match.params.orgId],
        SCHEMAS.ORGANIZATION,
        store.entities
      ),
      teamsQueryId,
      workspace: selectActiveWorkspace(store),
    };
  },
  {
    loadOrg: load,
    listTeams,
    listOrganizations,
  }
)(OrgManageRouter);
